// frontend/src/lib/reportePdf.ts
// Generación de PDFs institucionales en el cliente a partir de HTML
// (Nómina de Presentismo, Novedades y Licencias). Extraído de ReportesTab:
// iframe sandbox aislado, html2canvas (scale 3) por hoja, jsPDF A4 vertical con
// 10 mm de margen. jspdf / html2canvas se cargan bajo demanda (import dinámico)
// para no inflar el chunk principal.

// Escapa texto para interpolarlo en el HTML del reporte (datos de cadetes, etc.)
export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Sello oficial en Base64 para que html2canvas lo renderice offline (null si no está disponible). */
export async function cargarSelloBase64(): Promise<string | null> {
  try {
    const response = await fetch('/sello-mds-pba.png');
    if (!response.ok) return null;
    const imageBlob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(imageBlob);
    });
  } catch (err) {
    console.warn('No se pudo convertir la imagen del sello para el PDF:', err);
    return null;
  }
}

/** Bloque de sello + firma que va en la última hoja de los reportes. */
export function footerSelloFirmaHtml(selloBase64: string | null): string {
  return `
        <div class="footer">
          <div class="sello-box">
            ${selloBase64
              ? `<img src="${escapeHtml(selloBase64)}" alt="SELLO MDS PBA" class="sello-img" />`
              : `<div style="border: 2px solid #000; padding: 6px; font-weight: bold; font-size: 7px; text-align: center; margin-bottom: 3px;">
              MINISTERIO DE DESARROLLO DE LA COMUNIDAD<br>PROVINCIA DE BUENOS AIRES
            </div>`}
            <span class="sello-label">Sello de Constancia Oficial</span>
          </div>

          <div class="firma-box">
            <div class="firma-line">
              Firma y Aclaración de la Autoridad
            </div>
          </div>
        </div>
      `;
}

// Hoja A4 simulada: 750px de ancho útil dentro de un iframe de 794px (210 mm).
export const ESTILOS_REPORTE = `
            body {
              margin: 0;
              padding: 0;
              background-color: #fff;
              font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              color: #000;
              -webkit-print-color-adjust: exact;
            }
            .report-page {
              width: 750px;
              margin: 0 auto 20px auto;
              padding: 15px;
              box-sizing: border-box;
              background: #fff;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #000;
              padding-bottom: 6px;
              margin-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 12px;
            }
            th, td {
              border: 1px solid #000;
              padding: 4px 3px;
              text-align: left;
              font-size: 7px;
            }
            th {
              background-color: #f0f0f0;
              font-weight: 700;
              text-transform: uppercase;
              text-align: center;
            }
            .center {
              text-align: center;
            }
            .bold {
              font-weight: 700;
            }
            .font-inter {
              font-weight: 600;
            }
            .footer {
              display: flex;
              flex-direction: column;
              align-items: center;
              margin-top: 15px;
              width: 100%;
            }
            .sello-box {
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 180px;
            }
            .sello-img {
              height: 80px;
              object-fit: contain;
              margin-bottom: 3px;
            }
            .sello-label {
              font-size: 7px;
              color: #555;
              text-transform: uppercase;
              font-weight: 600;
            }
            .firma-box {
              display: flex;
              justify-content: flex-end;
              width: 100%;
              margin-top: 12px;
            }
            .firma-line {
              border-top: 1px dotted #000;
              width: 150px;
              text-align: center;
              padding-top: 3px;
              font-size: 8px;
              font-weight: 600;
              margin-right: 10px;
            }
`;

export interface OpcionesHtmlAPdf {
  // CSS adicional (se agrega después de ESTILOS_REPORTE)
  estilosExtra?: string;
}

/**
 * Convierte una lista de hojas HTML (cada una un `<div class="report-page">…</div>`)
 * en un PDF A4 vertical y lo descarga como `nombreArchivo`.
 * Cada hoja se rasteriza por separado (sin filas cortadas en el salto de página)
 * y se dibuja con la misma escala (190 mm de ancho) para que todas midan igual.
 */
export async function htmlAPdf(paginasHtml: string[], nombreArchivo: string, opciones: OpcionesHtmlAPdf = {}): Promise<void> {
  if (paginasHtml.length === 0) {
    throw new Error('No se generaron páginas para el PDF.');
  }
  const totalPaginas = paginasHtml.length;

  const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
${ESTILOS_REPORTE}
${opciones.estilosExtra || ''}
          </style>
        </head>
        <body>
          ${paginasHtml.join('')}
        </body>
        </html>
      `;

  // Crear un iframe temporal totalmente aislado en formato A4 Vertical
  const iframe = document.createElement('iframe');
  // Sandbox restrictivo: sin scripts (el HTML no los necesita). Se mantiene
  // allow-same-origin porque html2canvas necesita acceder al DOM del iframe.
  iframe.setAttribute('sandbox', 'allow-same-origin');
  iframe.style.position = 'fixed';
  iframe.style.left = '0';
  iframe.style.top = '0';
  iframe.style.width = '794px'; // Ancho simulado equivalente a 210mm
  iframe.style.height = `${Math.max(1123, totalPaginas * 1180)}px`; // Alto para apilar todas las hojas
  iframe.style.zIndex = '-9999';
  iframe.style.pointerEvents = 'none';
  iframe.style.border = 'none';
  document.body.appendChild(iframe);

  try {
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      throw new Error('No se pudo acceder al documento del iframe.');
    }

    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    // Esperar un breve instante para que el navegador dibuje los estilos en el iframe
    await new Promise((resolve) => setTimeout(resolve, 150));

    const pageElements = Array.from(iframeDoc.querySelectorAll('.report-page')) as HTMLElement[];
    if (pageElements.length === 0) {
      throw new Error('No se generaron páginas para el PDF.');
    }

    const [{ jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const margin = 10; // mm
    const maxWidth = 210 - margin * 2; // 190mm: ancho imprimible A4
    const maxHeight = 297 - margin * 2; // 277mm: alto imprimible A4

    // Una hoja A4 por .report-page — cada una se rasteriza por separado, así no
    // hay duplicación ni filas cortadas en el salto de página.
    for (let i = 0; i < pageElements.length; i++) {
      const canvas = await html2canvas(pageElements[i], {
        scale: 3, // Calidad Retina HD; bajar a 2 si hay lentitud en nóminas largas
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: 794, // Ancho simulado alineado al iframe
      });

      // Escala FIJA en todas las hojas: ancho imprimible A4 (190mm). Como cada
      // .report-page mide lo mismo (750px) y nunca se reduce el ancho, todas las
      // hojas salen exactamente del mismo tamaño (misma escala mm/px).
      const imgWidth = maxWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const x = margin;
      // Normalmente arriba con el margen estándar. Si la última hoja (la del sello)
      // supera el área imprimible, se centra en el A4 físico SIN achicarla.
      const y = imgHeight <= maxHeight ? margin : Math.max(2, (297 - imgHeight) / 2);

      if (i > 0) doc.addPage();
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgWidth, imgHeight);
    }

    doc.save(nombreArchivo);
  } finally {
    document.body.removeChild(iframe);
  }
}

/** Descarga un Blob como archivo (patrón <a download>, revoca la URL diferido para Safari). */
export function descargarBlob(blob: Blob, nombreArchivo: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombreArchivo);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
